/**
 * Fotos de progreso en la base (Fase 6, 0.14.0; design.md 7.10; Req. 6.17).
 *
 * Dos tablas y una sola transacción: los datos en `photos` y los bytes del
 * JPEG en `photoBlobs`, con el mismo id. Nunca queda una sin la otra, y los
 * bytes nunca se acercan al JSON del respaldo: ahí un Blob se volvería `{}`
 * y la foto se perdería en silencio.
 *
 * Las decisiones de cuánto se reduce cada foto, cómo se agrupa la galería y
 * qué se verifica al restaurar están en `@/domain/fotos`, que es código puro.
 */

import { db } from './schema';
import { enEscritura, enLectura } from './tx';
import { sha256DeBytes } from './checksum';
import type { Photo } from './types';
import { ahoraIso, esFechaLocal, type FechaLocal } from '@/domain/dates';
import { ErrorValidacion } from '@/domain/errors';
import { NOTA_MAX, POSES, porDia, type DiaDeFotos, type Pose } from '@/domain/fotos';

// ---------------------------------------------------------------------------
// Lectura
// ---------------------------------------------------------------------------

/** LECTURA PURA. Los datos de todas las fotos, sin sus bytes. */
export async function leerFotos(): Promise<Photo[]> {
  return db.photos.toArray();
}

/** LECTURA PURA. La galería por día, el más reciente primero. */
export async function leerGaleria(): Promise<DiaDeFotos[]> {
  return porDia(await db.photos.toArray());
}

/** LECTURA PURA. */
export async function leerFoto(id: string): Promise<Photo | undefined> {
  return db.photos.get(id);
}

/**
 * LECTURA PURA. Los bytes de una foto, para mostrarla o meterla al respaldo.
 * `undefined` si la foto no está o si sus bytes faltan.
 */
export async function leerBytesDeFoto(id: string): Promise<Blob | undefined> {
  return (await db.photoBlobs.get(id))?.datos;
}

/** LECTURA PURA. Cuántas fotos hay y cuánto ocupan, para la pantalla. */
export async function espacioDeFotos(): Promise<{ fotos: number; bytes: number }> {
  const fotos = await db.photos.toArray();
  return { fotos: fotos.length, bytes: fotos.reduce((suma, f) => suma + (f.bytes || 0), 0) };
}

/**
 * LECTURA PURA. Fotos cuyos datos están pero sus bytes no. No debería pasar:
 * se guardan y se borran juntas en la misma transacción. Se revisa igual,
 * porque un respaldo editado a mano o un almacenamiento lleno a la mitad del
 * guardado lo dejarían así, y es mejor decirlo que mostrar un hueco.
 */
export async function fotosSinBytes(): Promise<Photo[]> {
  const fotos = await enLectura([db.photos, db.photoBlobs], async () => {
    const datos = await db.photos.toArray();
    const conBytes = new Set(await db.photoBlobs.toCollection().primaryKeys());
    return datos.filter((f) => !conBytes.has(f.id));
  });
  return fotos;
}

// ---------------------------------------------------------------------------
// Guardar
// ---------------------------------------------------------------------------

export interface FotoACapturar {
  /** Lo crea la pantalla al abrirse: dos toques no duplican (Req. 1.9). */
  id: string;
  fecha: FechaLocal;
  pose: Pose;
  nota: string;
  /** El JPEG YA reducido (`reducirImagen` en lib/imagen.ts). */
  datos: Blob;
}

function revisarFecha(fecha: FechaLocal, hoy: FechaLocal): void {
  if (!esFechaLocal(fecha)) throw new ErrorValidacion('La fecha no es válida.', 'fecha');
  if (fecha > hoy) {
    throw new ErrorValidacion('La foto no puede quedar en un día que todavía no llega.', 'fecha');
  }
}

function revisarNota(nota: string): string | null {
  const limpia = nota.trim();
  if (limpia.length > NOTA_MAX) {
    throw new ErrorValidacion(`La nota puede tener hasta ${NOTA_MAX} caracteres.`, 'nota');
  }
  return limpia === '' ? null : limpia;
}

function revisarPose(pose: Pose): void {
  if (!POSES.includes(pose)) throw new ErrorValidacion('Esa pose no existe.', 'pose');
}

/**
 * Guarda una foto: sus datos y sus bytes, en la misma transacción. ESCRIBE.
 *
 * El SHA-256 se calcula aquí, sobre los bytes que se van a guardar, y es el
 * que después viajará en el respaldo: si se calculara al exportar, una foto
 * que se dañó en el almacenamiento pasaría la verificación sin que nadie se
 * diera cuenta.
 */
export async function guardarFoto(f: FotoACapturar, hoy: FechaLocal): Promise<Photo> {
  revisarFecha(f.fecha, hoy);
  revisarPose(f.pose);
  const nota = revisarNota(f.nota);
  if (f.datos.size === 0) throw new ErrorValidacion('La foto quedó vacía. Tómala otra vez.');

  const sha256 = await sha256DeBytes(await f.datos.arrayBuffer());
  const ahora = ahoraIso();

  return enEscritura([db.photos, db.photoBlobs], async () => {
    const existente = await db.photos.get(f.id);
    const foto: Photo = {
      id: f.id,
      fecha: f.fecha,
      momento: existente?.momento ?? ahora,
      pose: f.pose,
      tipo: 'image/jpeg',
      bytes: f.datos.size,
      sha256,
      nota,
      creadoEn: existente?.creadoEn ?? ahora,
    };
    await db.photoBlobs.put({ id: f.id, datos: f.datos });
    await db.photos.put(foto);
    return foto;
  });
}

export interface CambiosDeFoto {
  fecha: FechaLocal;
  pose: Pose;
  nota: string;
}

/**
 * Corrige la fecha, la pose o la nota de una foto. Los bytes no se tocan: la
 * imagen no se edita nunca. ESCRIBE.
 */
export async function corregirFoto(
  id: string,
  cambios: CambiosDeFoto,
  hoy: FechaLocal,
): Promise<Photo> {
  revisarFecha(cambios.fecha, hoy);
  revisarPose(cambios.pose);
  const nota = revisarNota(cambios.nota);

  return enEscritura([db.photos], async () => {
    const actual = await db.photos.get(id);
    if (actual === undefined) throw new ErrorValidacion('Esa foto ya no existe.');
    const foto: Photo = { ...actual, fecha: cambios.fecha, pose: cambios.pose, nota };
    await db.photos.put(foto);
    return foto;
  });
}

/**
 * Borra una foto con sus bytes; la pantalla ya pidió confirmación. ESCRIBE.
 *
 * Es el único lugar que borra una foto: no hay límite de cuántas caben ni
 * borrado automático de las viejas. Nada se borra solo.
 */
export async function borrarFoto(id: string): Promise<void> {
  await enEscritura([db.photos, db.photoBlobs], async () => {
    await db.photos.delete(id);
    await db.photoBlobs.delete(id);
  });
}
